import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FileText, MessageSquare, History,
  CheckCircle2, XCircle, Send, RefreshCw,
  AlertCircle, AlertTriangle,
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/empty-state'
import { RecordStatusBadge } from './record-status-badge'
import { RecordFormsTab } from './record-forms-tab'
import { RecordCommentsTab } from './record-comments-tab'
import { RecordAuditTab } from './record-audit-tab'
import { ApproveRecordDialog } from '@/features/review/components/approve-dialog'
import { DeclineRecordDialog } from '@/features/review/components/decline-dialog'
import { useRecordRealtime } from '../api/use-record-realtime'
import {
  useRecordQuery,
  useApproveRecordMutation,
  useDeclineRecordMutation,
  useCheckFinalSubmitMutation,
  useFinalSubmitCheckQuery,
  useFinalSubmitMutation,
  useTransitionToUnderReviewMutation,
  useDeclineCommentsQuery,
} from '../api/use-records-query'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useToast } from '@/hooks/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import type { FinalSubmitCheck } from '../api/records-api'
import { ConflictError, OpenDeclineCommentsError, discardDraft, reopenRecord } from '../api/records-api'
import { Textarea } from '@/components/ui/textarea'
import { ConflictDialog } from './conflict-dialog'
import { SummaryCardStrip } from '@/features/summary-card/components/summary-card-strip'
import { RECORD_STATUS_CONFIG, RECORD_STATUS_BANNER_TEXT } from '../lib/status-config'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

// ─── FinalSubmitDialog ────────────────────────────────────────────────────────

interface FinalSubmitDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  isLoading: boolean
  result: FinalSubmitCheck | null
}

export function FinalSubmitDialog({ open, onClose, onConfirm, isLoading, result }: FinalSubmitDialogProps) {
  const hasMissingForms     = result ? result.missingForms.length > 0 : false
  const hasOpenDecline      = result ? result.openDeclineComments > 0 : false
  const hasOrphanDrafts     = result ? result.orphanDrafts.length > 0 : false
  const hasDeprecated       = result ? result.deprecatedSubmissions.length > 0 : false
  const blocked = !!result && !result.success
  const totalOrphans = (result?.orphanDrafts ?? []).reduce((n, d) => n + d.draftCount, 0)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            ارسال نهایی پرونده
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {!result || (result.success && !hasOrphanDrafts) ? (
            <p className="text-sm text-muted-foreground">
              پرونده برای بررسی توسط تأییدکننده ارسال می‌شود. پس از ارسال، تا اعلام نظر تأییدکننده قابل ویرایش نیست.
            </p>
          ) : null}

          {hasMissingForms && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-yellow-400">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>فرم‌های زیر هنوز ارسال نشده‌اند:</p>
              </div>
              <ul className="space-y-1 border border-yellow-500/20 rounded-lg p-3 bg-yellow-500/5">
                {result!.missingForms.map((name, i) => (
                  <li key={i} className="text-sm text-foreground flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasOpenDecline && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">
                {result!.openDeclineComments.toLocaleString('fa-IR')} نظر اصلاحی هنوز حل نشده. ابتدا تمام نظرات اصلاحی را حل کنید.
              </p>
            </div>
          )}

          {hasDeprecated && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-red-400">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                  {result!.deprecatedSubmissions.length.toLocaleString('fa-IR')} فرم روی نسخه قدیمی پر شده‌اند و باید قبل از ارسال نهایی به‌روزرسانی شوند:
                </p>
              </div>
              <ul className="space-y-1 border border-red-500/20 rounded-lg p-3 bg-red-500/5">
                {result!.deprecatedSubmissions.map((d) => (
                  <li key={d.submissionId} className="text-sm text-foreground flex items-center justify-between gap-2">
                    <span>
                      {d.formTitle}
                      {d.instanceIndex > 1 && (
                        <span className="text-muted-foreground ms-1">#{d.instanceIndex.toLocaleString('fa-IR')}</span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">v{d.pinnedVersion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasOrphanDrafts && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-sky-400">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                  {totalOrphans.toLocaleString('fa-IR')} پیش‌نویس ناتمام پیدا شد. این‌ها از روند ارسال نهایی خارج می‌مانند مگر اینکه آن‌ها را ارسال یا حذف کنید.
                </p>
              </div>
              <ul className="space-y-1 border border-sky-500/20 rounded-lg p-3 bg-sky-500/5">
                {result!.orphanDrafts.map((d, i) => (
                  <li key={i} className="text-xs text-foreground flex items-center justify-between gap-2">
                    <span>
                      {d.formTitle}
                      {d.optional && <span className="text-muted-foreground ms-1">(اختیاری)</span>}
                    </span>
                    <span className="text-muted-foreground">
                      {d.draftCount.toLocaleString('fa-IR')} پیش‌نویس
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>انصراف</Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading || blocked}
          >
            {isLoading ? 'در حال ارسال...' : 'ارسال نهایی'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── RecordDetailPanel ────────────────────────────────────────────────────────

interface RecordDetailPanelProps {
  projectId: string
  recordId: string
  /** Fixed px width in the 3-pane layout; omit for full-width (narrow mode). */
  width?: number
  onFormSelect: (formId: string, submissionId?: string) => void
}

export function RecordDetailPanel({
  projectId,
  recordId,
  width,
  onFormSelect,
}: RecordDetailPanelProps) {
  const sizeStyle = width != null ? { width } : undefined
  const sizeCls = width != null ? 'shrink-0' : 'flex-1 min-w-0'
  const { canApprove, canEdit, role } = useProjectRole()
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const { toast } = useToast()

  // URL-driven tab so the selection survives refetches / re-renders and is
  // bookmarkable. Defaults to "forms".
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'forms'
  const setActiveTab = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', value)
    setSearchParams(next, { replace: true })
  }

  useRecordRealtime(projectId, recordId)
  const { data: record, isLoading, refetch } = useRecordQuery(recordId)
  const approveMutation = useApproveRecordMutation(projectId)
  const declineMutation = useDeclineRecordMutation(projectId)
  const checkFinalSubmitMutation = useCheckFinalSubmitMutation(projectId)
  const finalSubmitMutation = useFinalSubmitMutation(projectId)
  const underReviewMutation = useTransitionToUnderReviewMutation(projectId)
  const { data: declineComments = [] } = useDeclineCommentsQuery(
    record?.status === 'needs_revision' ? recordId : undefined
  )

  // Cached precondition check — drives progress bar + final-submit button
  // enablement without re-firing the RPC on every click.
  const { data: liveCheck } = useFinalSubmitCheckQuery(
    record &&
      ['draft','in_progress','needs_revision'].includes(record.status)
      ? recordId
      : undefined,
    projectId,
  )

  const [approveOpen, setApproveOpen] = useState(false)
  const [declineOpen, setDeclineOpen] = useState(false)
  const [finalSubmitOpen, setFinalSubmitOpen] = useState(false)
  const [finalSubmitResult, setFinalSubmitResult] = useState<FinalSubmitCheck | null>(null)
  const [conflictState, setConflictState] = useState<{ version: number; status: string } | null>(null)
  const [reopenOpen, setReopenOpen] = useState(false)
  const [reopenReason, setReopenReason] = useState('')
  const [reopenPending, setReopenPending] = useState(false)

  const isAdmin = role === 'admin'
  // Admin reopen available on terminal/locked records.
  const canReopen = isAdmin && record &&
    (record.status === 'approved' || record.status === 'needs_revision')

  const handleReopen = async () => {
    if (!user || !record) return
    setReopenPending(true)
    try {
      await reopenRecord(record.id, user.id, projectId, reopenReason.trim() || undefined)
      setReopenOpen(false)
      setReopenReason('')
      toast({ title: 'سابقه بازگشایی شد', description: 'اکنون قابل ویرایش است.' })
      refetch()
      qc.invalidateQueries({ queryKey: ['records', projectId] })
      qc.invalidateQueries({ queryKey: ['final-submit-check', recordId] })
    } catch (err) {
      if (err instanceof ConflictError) {
        setReopenOpen(false)
        setConflictState({ version: err.currentVersion, status: err.currentStatus })
        return
      }
      toast({ title: 'خطا در بازگشایی', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setReopenPending(false)
    }
  }

  // Auto-transition submitted_for_review → under_review when approver opens.
  // Surface failures so approvers see a forbidden/locked situation rather than
  // silently sitting on a stuck record.
  useEffect(() => {
    if (canApprove && record?.status === 'submitted_for_review' && user && projectId && recordId) {
      underReviewMutation.mutate(
        { recordId, userId: user.id, userRole: role ?? 'approver' },
        {
          onError: (err) => {
            toast({
              title: 'خطا در ورود به بررسی',
              description: (err as Error).message,
              variant: 'destructive',
            })
          },
        },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.status, canApprove])

  const canActOnRecord =
    canApprove &&
    record &&
    (record.status === 'submitted_for_review' || record.status === 'under_review')

  const canFinalSubmit =
    (canEdit || role === 'admin') &&
    record &&
    (record.status === 'draft' || record.status === 'in_progress' || record.status === 'needs_revision')

  const handleFinalSubmitClick = async () => {
    if (!user || !record) return
    setFinalSubmitResult(null)
    setFinalSubmitOpen(true)
    try {
      const result = await checkFinalSubmitMutation.mutateAsync({ recordId: record.id })
      setFinalSubmitResult(result)
    } catch (err) {
      setFinalSubmitOpen(false)
      toast({
        title: 'خطا در بررسی پیش‌نیازهای ارسال نهایی',
        description: (err as Error).message,
        variant: 'destructive',
      })
    }
  }

  const handleFinalSubmitConfirm = async () => {
    if (!user || !record || !finalSubmitResult?.success) return
    try {
      await finalSubmitMutation.mutateAsync({
        recordId: record.id,
        userId: user.id,
        userRole: role ?? 'data_entry',
      })
      setFinalSubmitOpen(false)
      toast({ title: 'پرونده با موفقیت ارسال شد' })
      refetch()
      qc.invalidateQueries({ queryKey: ['records', projectId] })
    } catch (err) {
      if (err instanceof ConflictError) {
        setFinalSubmitOpen(false)
        setConflictState({ version: err.currentVersion, status: err.currentStatus })
        return
      }
      toast({
        title: 'خطا در ارسال نهایی',
        description: (err as Error).message,
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div style={sizeStyle} className={cn(sizeCls, 'h-full flex flex-col border-e border-border p-4 space-y-3')}>
        <div className="h-8 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
        <div className="h-32 bg-muted rounded animate-pulse" />
      </div>
    )
  }

  if (!record) {
    return (
      <div style={sizeStyle} className={cn(sizeCls, 'h-full flex flex-col border-e border-border items-center justify-center')}>
        <EmptyState icon={FileText} title="سابقه یافت نشد" />
      </div>
    )
  }

  return (
    <div style={sizeStyle} className={cn(sizeCls, 'h-full flex flex-col overflow-hidden')}>
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 mb-2 min-w-0">
          <span className="font-mono text-sm font-bold text-foreground truncate">{record.display_id}</span>
          <RecordStatusBadge status={record.status} />
          {/* G1 — derived UI flag: data-entry has filled everything required.
              No DB enum needed; the precondition check already knows. Live
              changes arrive via useRecordRealtime, so no manual refresh button. */}
          {canFinalSubmit && liveCheck?.success && (
            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded shrink-0">
              آماده ارسال نهایی
            </span>
          )}
        </div>

        {/* I3 — progress bar (only while record is still editable) */}
        {canFinalSubmit && liveCheck && liveCheck.totalRequiredForms > 0 && (
          <div className="mb-2">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
              <span>
                {(liveCheck.totalRequiredForms - liveCheck.missingForms.length).toLocaleString('fa-IR')}
                {' / '}
                {liveCheck.totalRequiredForms.toLocaleString('fa-IR')} فرم اجباری تکمیل شده
              </span>
              {liveCheck.openDeclineComments > 0 && (
                <span className="text-red-400">{liveCheck.openDeclineComments.toLocaleString('fa-IR')} نظر اصلاحی باز</span>
              )}
            </div>
            <div className="h-1 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all',
                  liveCheck.success ? 'bg-emerald-500' : 'bg-yellow-500',
                )}
                style={{
                  width: `${Math.round(
                    ((liveCheck.totalRequiredForms - liveCheck.missingForms.length) /
                      Math.max(liveCheck.totalRequiredForms, 1)) * 100,
                  )}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {canFinalSubmit && (
            <Button
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={handleFinalSubmitClick}
              // I5 — block when preconditions fail; tooltip carries the reason
              disabled={
                checkFinalSubmitMutation.isPending ||
                finalSubmitMutation.isPending ||
                (!!liveCheck && !liveCheck.success)
              }
              title={
                liveCheck && !liveCheck.success
                  ? [
                      liveCheck.missingForms.length > 0
                        ? `فرم‌های ناقص: ${liveCheck.missingForms.join('، ')}`
                        : null,
                      liveCheck.openDeclineComments > 0
                        ? `${liveCheck.openDeclineComments} نظر اصلاحی باز`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' — ')
                  : undefined
              }
            >
              <Send className="w-3 h-3" />
              {/* no-review projects "complete" the record; review projects send it onward */}
              {liveCheck?.reviewMode === 'none' ? 'ثبت نهایی' : 'ارسال برای بررسی'}
            </Button>
          )}
          {canReopen && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={() => setReopenOpen(true)}
            >
              <RefreshCw className="w-3 h-3" />
              بازگشایی
            </Button>
          )}
          {canActOnRecord && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                onClick={() => setApproveOpen(true)}
              >
                <CheckCircle2 className="w-3 h-3" />
                تأیید
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 text-red-400 border-red-500/30 hover:bg-red-500/10"
                onClick={() => setDeclineOpen(true)}
              >
                <XCircle className="w-3 h-3" />
                رد
              </Button>
            </>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground mt-1.5">
          ایجاد: {dayjs(record.created_at).format('YYYY/MM/DD')}
          {record.submitted_at && ` · ارسال: ${dayjs(record.submitted_at).format('YYYY/MM/DD')}`}
        </p>
      </div>

      {/* Cross-form summary card — at-a-glance record identification, driven by
          the project's summary_card_config (08_summary_card.md). */}
      <SummaryCardStrip projectId={projectId} recordId={recordId} />

      {/* Status banner — color + copy from the shared status config. */}
      {RECORD_STATUS_BANNER_TEXT[record.status] && (
        <div className={cn(
          'shrink-0 border-b px-4 py-2',
          RECORD_STATUS_CONFIG[record.status].bannerClass,
        )}>
          <p className="text-xs font-medium">
            {RECORD_STATUS_BANNER_TEXT[record.status]}
            {record.status === 'needs_revision' && declineComments.length > 0 && (
              <span className="font-normal ms-1">· {declineComments.length} نظر باز</span>
            )}
          </p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} dir="rtl" className="flex-1 flex flex-col overflow-hidden min-h-0">
        <TabsList className="shrink-0 w-full justify-start rounded-none border-b border-border bg-transparent h-auto px-2 gap-0">
          {[
            { value: 'forms', label: 'فرم‌ها', icon: FileText },
            { value: 'comments', label: 'نظرات', icon: MessageSquare },
            { value: 'audit', label: 'تاریخچه', icon: History },
          ].map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground px-2 py-2 gap-1 text-xs"
            >
              <Icon className="w-3 h-3" />
              {label}
              {value === 'comments' && declineComments.length > 0 && (
                <span className="w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[8px] flex items-center justify-center">
                  {declineComments.length}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="forms" className="p-3 mt-0">
            <RecordFormsTab
              projectId={projectId}
              recordId={recordId}
              onFormSelect={(formId, submissionId) => onFormSelect(formId, submissionId)}
            />
          </TabsContent>
          <TabsContent value="comments" className="p-3 mt-0">
            <RecordCommentsTab recordId={recordId} />
          </TabsContent>
          <TabsContent value="audit" className="p-3 mt-0">
            <RecordAuditTab recordId={recordId} projectId={projectId} />
          </TabsContent>
        </div>
      </Tabs>

      {/* Dialogs */}
      <ApproveRecordDialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={async (comment) => {
          if (!user) return
          try {
            await approveMutation.mutateAsync({
              recordId: record.id,
              userId: user.id,
              userRole: role ?? 'approver',
              comment,
            })
            setApproveOpen(false)
            toast({ title: 'پرونده تأیید شد' })
            refetch()
            qc.invalidateQueries({ queryKey: ['records', projectId] })
          } catch (e) {
            if (e instanceof ConflictError) {
              setApproveOpen(false)
              setConflictState({ version: e.currentVersion, status: e.currentStatus })
            } else if (e instanceof OpenDeclineCommentsError) {
              setApproveOpen(false)
              toast({
                title: 'تأیید امکان‌پذیر نیست',
                description: 'پیش از تأیید باید همه نظرات رد را برطرف کنید',
                variant: 'destructive',
              })
            } else {
              throw e
            }
          }
        }}
        isLoading={approveMutation.isPending}
      />
      <DeclineRecordDialog
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={async (comment) => {
          if (!user) return
          try {
            await declineMutation.mutateAsync({
              recordId: record.id,
              userId: user.id,
              userRole: role ?? 'approver',
              comment,
            })
            setDeclineOpen(false)
            toast({ title: 'پرونده رد شد' })
            refetch()
            qc.invalidateQueries({ queryKey: ['records', projectId] })
          } catch (e) {
            if (e instanceof ConflictError) {
              setDeclineOpen(false)
              setConflictState({ version: e.currentVersion, status: e.currentStatus })
            } else {
              throw e
            }
          }
        }}
        isLoading={declineMutation.isPending}
      />
      <FinalSubmitDialog
        open={finalSubmitOpen}
        onClose={() => { setFinalSubmitOpen(false); setFinalSubmitResult(null) }}
        onConfirm={handleFinalSubmitConfirm}
        isLoading={checkFinalSubmitMutation.isPending || finalSubmitMutation.isPending}
        result={finalSubmitResult}
      />
      <ConflictDialog
        open={!!conflictState}
        currentVersion={conflictState?.version ?? 0}
        currentStatus={conflictState?.status ?? ''}
        onReload={() => { setConflictState(null); refetch(); qc.invalidateQueries({ queryKey: ['records', projectId] }) }}
        onClose={() => setConflictState(null)}
      />

      <Dialog open={reopenOpen} onOpenChange={(o) => { if (!o) { setReopenOpen(false); setReopenReason('') } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-primary" />
              بازگشایی سابقه
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              سابقه به حالت «در حال تکمیل» برمی‌گردد و قابل ویرایش می‌شود.
              {record?.status === 'approved' && ' تأیید فعلی لغو خواهد شد.'}
            </p>
            <Textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="دلیل بازگشایی (اختیاری، در تاریخچه ثبت می‌شود)..."
              rows={2}
              className="text-xs resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReopenOpen(false); setReopenReason('') }}>انصراف</Button>
            <Button onClick={handleReopen} disabled={reopenPending}>
              {reopenPending ? 'در حال بازگشایی...' : 'بازگشایی'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
